import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { loginSchema } from '@massar/shared/schemas';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/features/auth/AuthContext';
import { LanguageToggle } from '@/components/layout/LanguageToggle';
import { ThemeToggle } from '@/components/layout/ThemeToggle';

/**
 * The only unauthenticated screen.
 *
 * `loginSchema` is imported from `packages/shared` rather than restated here, so the
 * client and the API cannot drift on what a valid credential looks like.
 */
export const LoginPage = () => {
  const { t } = useTranslation(['auth', 'common']);
  const { login, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  /** The server only asks for a TOTP once it knows the password was right. */
  const [mfaRequired, setMfaRequired] = React.useState(false);
  const [formError, setFormError] = React.useState(null);

  const {
    register,
    handleSubmit,
    setFocus,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  React.useEffect(() => {
    setFocus(mfaRequired ? 'totp' : 'email');
  }, [setFocus, mfaRequired]);

  if (isAuthenticated) {
    return <Navigate to={location.state?.from || '/'} replace />;
  }

  const onSubmit = async (values) => {
    setFormError(null);
    try {
      await login({ ...values, totp: values.totp || undefined });
      navigate(location.state?.from || '/', { replace: true });
    } catch (error) {
      if (error.code === 'MFA_REQUIRED') {
        setMfaRequired(true);
        setFormError(t('auth:errors.MFA_REQUIRED'));
        return;
      }
      setFormError(t(`auth:errors.${error.code}`, { defaultValue: t('auth:errors.generic') }));
    }
  };

  return (
    <div className="flex min-h-dvh flex-col bg-muted/30">
      <header className="flex items-center justify-end gap-1 p-4">
        <LanguageToggle />
        <ThemeToggle />
      </header>

      <main className="flex flex-1 items-start justify-center px-4 pb-16 pt-6 sm:items-center sm:pt-0">
        <Card className="w-full max-w-sm">
          <CardHeader className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="flex size-8 items-center justify-center rounded-md bg-primary text-sm font-bold text-primary-foreground">
                M
              </div>
              <div>
                <CardTitle>{t('common:app.name')}</CardTitle>
                <CardDescription className="text-caption">{t('common:app.tagline')}</CardDescription>
              </div>
            </div>
            <CardDescription>{t('auth:login.subtitle')}</CardDescription>
          </CardHeader>

          <CardContent>
            {isLoading ? (
              <p className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" />
                {t('auth:login.restoring')}
              </p>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
                <div className="space-y-1.5">
                  <Label htmlFor="email">{t('auth:login.email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="username"
                    dir="ltr"
                    aria-invalid={!!errors.email}
                    {...register('email')}
                  />
                  {errors.email ? (
                    <p className="text-caption text-destructive">{errors.email.message}</p>
                  ) : null}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="password">{t('auth:login.password')}</Label>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    dir="ltr"
                    aria-invalid={!!errors.password}
                    {...register('password')}
                  />
                  {errors.password ? (
                    <p className="text-caption text-destructive">{errors.password.message}</p>
                  ) : null}
                </div>

                {mfaRequired ? (
                  <div className="space-y-1.5">
                    <Label htmlFor="totp">{t('auth:login.totp')}</Label>
                    <Input
                      id="totp"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      dir="ltr"
                      className="text-center font-mono tracking-[0.4em]"
                      {...register('totp')}
                    />
                    <p className="text-caption text-muted-foreground">{t('auth:login.totpHint')}</p>
                  </div>
                ) : null}

                {formError ? (
                  <p
                    role="alert"
                    className="rounded-md bg-destructive/10 p-2.5 text-caption text-destructive"
                  >
                    {formError}
                  </p>
                ) : null}

                <Button type="submit" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? <Loader2 className="size-4 animate-spin" /> : null}
                  {t('auth:login.submit')}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default LoginPage;
